import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import styles from "./AlaskaTab.module.css";
import ProductHeader from "../ProductHeader/ProductHeader";
import FeaturesList from "../FeaturesList/FeaturesList";
import PreviewItem from "../PreviewItem/PreviewItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Icons from "@fortawesome/free-solid-svg-icons";
import CalendarView from "../../assets/calendar-view.png";
import BidPacket from "../../assets/bid-packet-view.png";
import BrowserView from "../../assets/browser-view.png";
import { CSSTransition } from "react-transition-group";
import { StaticRoutes } from "../../pages/routes";

const AlaskaTab = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const acceptingSignUps = false;
  const collapseRef = useRef(null);

  const toggleCollapse = (e) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const features = [
    {
      icon: "fa-check",
      header: "Line Hold Status",
      description:
        "View the hold status of each line to get a better idea of which lines you may be awarded – before even submitting your bids.",
    },
    {
      icon: "fa-cogs",
      header: "Customization",
      description:
        "Make the app your own by customizing your colors, layout, and list names.",
    },
    {
      icon: "fa-download",
      header: "Updates",
      description:
        "Free updates are frequently released to the App Store with new features and bug fixes.",
    },
  ];

  const carouselImgs = [
    { src: CalendarView, alt: "Calendar View" },
    { src: BidPacket, alt: "Bid Packet View" },
    { src: BrowserView, alt: "Browser View" },
  ];

  const sortLinesChildren = (
    <>
      <small className={styles.small}>
        Lists can be sorted by pay, days off, total credit, and{" "}
        <a
          href="#AlaskaSorts"
          onClick={toggleCollapse}
          role="button"
          aria-expanded="false"
        >
          more ▼{" "}
        </a>
      </small>
      <CSSTransition
        in={isOpen}
        timeout={300}
        classNames={{
          enter: styles.slideEnter,
          enterActive: styles.slideEnterActive,
          exit: styles.slideExit,
          exitActive: styles.slideExitActive,
        }}
        unmountOnExit
        nodeRef={collapseRef}
      >
        <div
          ref={collapseRef}
          className={`collapse ${isOpen ? "show" : ""} ${styles.collapse}`}
          id="AlaskaSorts"
        >
          <div className="card card-body">
            <h6>Available sort criteria for Alaska pilots:</h6>
            <ul className={`ul ${styles.sortOptions}`}>
              <li>Pay</li>
              <li>Total credit</li>
              <li>Total expenses</li>
              <li>Total block</li>
              <li>Days off</li>
              <li>Dead head</li>
              <li>Carry out</li>
              <li>Time away from base</li>
              <li>FDP</li>
              <li>Reserved</li>
            </ul>
          </div>
        </div>
      </CSSTransition>
    </>
  );

  const previewItemFooter = (
    <>
      Want to learn more?{" "}
      <Link to="https://airlinedata.blob.core.windows.net/corporate/ipad_tutorial.pdf">
        View the Alaska Tutorial{" "}
        <FontAwesomeIcon
          className={styles.faIcon}
          icon={Icons.faArrowUpRightFromSquare}
        />
      </Link>
    </>
  );

  const onClick = () => {
    navigate(StaticRoutes.ASPricing);
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.headerRow}>
        <ProductHeader
          title={"Alaska Airlines"}
          description={
            "BidPro has been working with Alaska Airlines since 2012 to provide advanced bidding solutions to Alaska pilots."
          }
          img={"calendar-view.png"}
        />
      </div>
      <div className={styles.row}>
        <FeaturesList features={features} colorBlue={true} />
      </div>
      <div className={styles.row}>
        <PreviewItem
          imgRight={true}
          header={"View Bid Data"}
          description={
            "Bid data is easily viewed in 3 different layouts – the Calendar view, Bid Packet view and Browser view."
          }
          imgs={carouselImgs}
        />
      </div>
      <div className={styles.row}>
        <PreviewItem
          imgRight={false}
          header={"Organize Your Bids into Lists"}
          description={
            "See some bid lines that you want to eliminate, or that you want to keep an eye on? Organize your bids by separating them into four different lists."
          }
          imgs={[
            {
              src: "alaska-move-lines.png",
              alt: "Graphic of moving lines on BidPro Plus! Bidding iPad app",
            },
          ]}
        />
      </div>
      <div className={styles.row}>
        <PreviewItem
          imgRight={true}
          header={"Sort Lines"}
          description={
            "Sorting allows you to quickly organize your lines in a way that works best for you. Each list is sorted individually without affecting your other lists."
          }
          imgs={[
            {
              src: "alaska-sort-criteria.png",
              alt: "Graphic of sorting criteria on BidPro Plus! Bidding iPad app",
            },
          ]}
          children={sortLinesChildren}
        />
      </div>
      <div className={styles.row}>
        <PreviewItem
          imgRight={false}
          header={"Submit Bids to Alaska"}
          description={
            "In just a couple of clicks, you can copy the line numbers from the list you'd like to submit, and then submit your bids to Alaska as normal."
          }
          imgs={[
            {
              src: "alaska-submit-bids.png",
              alt: "Graphic of submitting bids on BidPro Plus! Bidding iPad app",
            },
          ]}
          footer={previewItemFooter}
        />
      </div>

      {acceptingSignUps ? (
        <>
          <div className={styles.contactRow}>
            <h2 className={styles.h2}>Ready to get started?</h2>
          </div>
          <div className={styles.contactRow}>
            <button
              type="submit"
              className="btn btn-rounded btn-primary btn-lg"
              id={styles.button}
              onClick={onClick}
            >
              SIGN UP
            </button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.contactRow}>
            <h2>We're Sorry.</h2>
          </div>
          <div className={styles.contactRow}>
            <h3>Sign ups are currently unavailable for this airline.</h3>
          </div>
          <div className={styles.contactRow}>
            <p>
              Please contact us <a href="/contact">here.</a>
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default AlaskaTab;
